import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { FaFacebook, FaInstagram, FaLinkedin } from "react-icons/fa";

const ALLOXA_ORIGIN = "https://api.alloxa.ai";
const ALLOXA_PUBLIC_KEY = "8dede71e2bc5ca0838c067b5";
const ALLOXA_SCRIPT_SELECTOR = "script[data-alloxa-connect='true']";

const loadAlloxaWidgetScript = () => {
  if (window.AlloxaWidgetInitialized) {
    return Promise.resolve();
  }

  if (window.__alloxaWidgetLoadingPromise) {
    return window.__alloxaWidgetLoadingPromise;
  }

  window.__alloxaWidgetLoadingPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(ALLOXA_SCRIPT_SELECTOR);
    if (existing) {
      if (window.AlloxaWidgetInitialized) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Failed to load Alloxa widget script.")),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.src = `${ALLOXA_ORIGIN}/public/widget.js`;
    script.async = true;
    script.setAttribute("data-origin", ALLOXA_ORIGIN);
    script.setAttribute("data-public-key", ALLOXA_PUBLIC_KEY);
    script.setAttribute("data-alloxa-connect", "true");
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Alloxa widget script."));
    document.body.appendChild(script);
  });

  return window.__alloxaWidgetLoadingPromise;
};

const hideAlloxaWidgetUi = () => {
  const widgetContainers = document.querySelectorAll("[id^='alloxa-widget-']");
  widgetContainers.forEach((container) => {
    container.style.display = "none";
  });
};

// Forward ref to allow parent components to control visibility
const ContactForm = forwardRef((props, ref) => {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isButtonVisible, setIsButtonVisible] = useState(true);
  const [threadId, setThreadId] = useState(null);

  // Snackbar state for success or error messages
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  // Expose methods to parent components
  useImperativeHandle(ref, () => ({
    openForm: () => setIsFormVisible(true),
    closeForm: () => setIsFormVisible(false),
  }));

  useEffect(() => {
    const updateButtonVisibility = () => {
      if (window.innerWidth <= 768) {
        if (window.scrollY > 100) {
          setIsButtonVisible(true);
        } else {
          setIsButtonVisible(false);
        }
      } else {
        setIsButtonVisible(true);
      }
    };

    updateButtonVisibility();
    window.addEventListener("scroll", updateButtonVisibility);
    window.addEventListener("resize", updateButtonVisibility);

    return () => {
      window.removeEventListener("scroll", updateButtonVisibility);
      window.removeEventListener("resize", updateButtonVisibility);
    };
  }, []);

  useEffect(() => {
    if (!isFormVisible) return;
    loadAlloxaWidgetScript().catch((error) => {
      console.error("Unable to initialize Alloxa widget:", error);
    });

    const hideTimer = setTimeout(() => {
      hideAlloxaWidgetUi();
    }, 250);

    return () => clearTimeout(hideTimer);
  }, [isFormVisible]);

  const getAssistantText = (responseData) => {
    if (typeof responseData === "string") return responseData;
    if (responseData?.choices?.[0]?.message?.content) {
      return responseData.choices[0].message.content;
    }
    if (responseData?.data?.choices?.[0]?.message?.content) {
      return responseData.data.choices[0].message.content;
    }
    if (responseData?.message) return responseData.message;
    if (responseData?.reply) return responseData.reply;
    if (responseData?.reply?.message) return responseData.reply.message;
    if (responseData?.reply?.content) return responseData.reply.content;
    if (responseData?.response) return responseData.response;
    if (responseData?.data?.message) return responseData.data.message;
    if (responseData?.data?.reply) return responseData.data.reply;
    if (responseData?.data?.reply?.message) return responseData.data.reply.message;
    if (responseData?.data?.reply?.content) return responseData.data.reply.content;
    if (responseData?.data?.response) return responseData.data.response;
    return null;
  };

  const getConversationId = (responseData) => {
    return (
      responseData?.threadId ||
      responseData?.conversationId ||
      responseData?.caseId ||
      responseData?.data?.threadId ||
      responseData?.data?.conversationId ||
      null
    );
  };

  const callAlloxaBackend = async (userMessage) => {
    const endpoint = `${ALLOXA_ORIGIN}/api/public/webchat/send`;
    const conversationHistory = messages.map((msg) => ({
      role: msg.isUser ? "user" : "assistant",
      message: msg.text,
    }));

    const payload = {
      publicKey: ALLOXA_PUBLIC_KEY,
      message: userMessage,
      messageType: "text",
      conversationHistory,
    };

    if (threadId) {
      payload.threadId = threadId;
    }

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json().catch(() => ({}));
    const assistantText = getAssistantText(responseData);

    if (!response.ok || !assistantText) {
      throw new Error(
        responseData?.error || responseData?.message || "No assistant response received."
      );
    }

    const nextThreadId = getConversationId(responseData);
    if (nextThreadId) {
      setThreadId(nextThreadId);
    }

    return assistantText;
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setMessages((prevMessages) => [
      ...prevMessages,
      { text: userMessage, isUser: true },
    ]);
    setInput("");
    setLoading(true);

    try {
      await loadAlloxaWidgetScript();
      hideAlloxaWidgetUi();
      const assistantText = await callAlloxaBackend(userMessage);

      setMessages((prevMessages) => [
        ...prevMessages,
        { text: assistantText, isUser: false },
      ]);
    } catch (error) {
      console.error("Error fetching assistant response:", error);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          text: "Sorry, something went wrong. Please try again in a moment.",
          isUser: false,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {isButtonVisible && (
        <button
          className="contact-button"
          id="connectbuttoni"
          onClick={() => setIsFormVisible(true)}
        >
          CONNECT US
        </button>
      )}

      {isFormVisible && (
        <div className="contact-form-container contact-chat-only">
          <img
            src="/img/LogoAlloxa.png"
            alt="Alloxa Logo"
            className="contact-logo"
          />
          <button
            className="contact-close-button"
            onClick={() => setIsFormVisible(false)}
          >
            X
          </button>

          <div className="contact-info-top">
            <div className="contact-info-meta">
              <a href="mailto:info@shigjeta.com">info@shigjeta.com</a>
              <a href="tel:+38346192921">+38346192921</a>
            </div>
            <div className="contact-info-social">
              <a
                href="https://www.facebook.com/shigjeta.agency"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <FaFacebook />
              </a>
              <a
                href="https://www.instagram.com/shigjetallc"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <FaInstagram />
              </a>
              <a
                href="https://www.linkedin.com/company/shigjeta"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <FaLinkedin />
              </a>
            </div>
          </div>

          {/* Chatbot */}
          <div className="chat-container alloxa-chat">
            <div className="shigjeta-chat-header">
              <div>
                <h4>Shigjeta AI Assistant</h4>
                <p>Powered by Alloxa</p>
              </div>
              <span className="shigjeta-chat-status">Online</span>
            </div>

            <div className="messages shigjeta-messages">
              {messages.length === 0 && (
                <div className="shigjeta-chat-empty">
                  Start a conversation for fast support and guidance.
                </div>
              )}

              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={msg.isUser ? "user-message" : "bot-message"}
                >
                  {msg.text}
                </div>
              ))}
              {loading && (
                <div className="bot-message">Shigjeta Assistant is typing...</div>
              )}
            </div>

            <div className="shigjeta-chat-input-row">
              <textarea
                id="input1"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Write your message..."
                className="input1 shigjeta-input"
                rows="1"
              />
              <button
                className="chatbutton shigjeta-send"
                onClick={sendMessage}
                disabled={loading || !input.trim()}
              >
                Send
              </button>
            </div>
          </div>

          {/* Snackbar Component */}
          {snackbarVisible && (
            <div
              className="snackbar"
              style={{
                position: "fixed",
                top: "20px",
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: snackbarMessage.includes("success")
                  ? "#16a085"
                  : "#e74c3c",
                color: "white",
                padding: "15px 30px",
                borderRadius: "8px",
                fontSize: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                zIndex: 1001,
                animation: "fadeIn 0.5s ease-out",
              }}
            >
              {snackbarMessage}
              <button
                onClick={() => setSnackbarVisible(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "white",
                  cursor: "none",
                  fontSize: "16px",
                  cursor: "none",
                  fontWeight: "bold",
                  marginLeft: "15px", // Adds a gap between the text and the button
                }}
              >
                ✖
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default ContactForm;
